import type { MoneyFormatter } from "../../shared/money";
import { toNumber } from "../../shared/money";
import type { PricingRule } from "./meta";
import { isPlain, shownAmount, type Basis } from "./pricing";

/** How each rounding reads in a description, and in a table column (↑ up, ↓ down, ≈ to the nearest). */
const ROUNDS: Record<PricingRule["roundMode"], string> = { none: "", up: "rounds up to", down: "rounds down to", nearest: "rounds to" };
const ROUNDS_SHORT: Record<PricingRule["roundMode"], string> = { none: "", up: "↑", down: "↓", nearest: "≈" };

/** "20 min", "2 hr", "1.5 hr". */
export function duration(minutes: number) {
    if (minutes < 60) return `${Math.round(minutes * 100) / 100} min`;
    // "1 hr 6 min", not "1.1 hr": to the minute.
    const whole = Math.round(minutes);
    const hours = Math.floor(whole / 60);
    return whole % 60 ? `${hours} hr ${whole % 60} min` : `${hours} hr`;
}

/** How an item's price is set, in a few words: "Typed", "Cost + 35%", "Cost + $10.00 · 30 min labour · rounds up to $5". */
export function describePricing(rule: PricingRule | undefined, basis: Basis, fmt: MoneyFormatter): string {
    if (!rule || isPlain(rule)) return "Typed";
    const parts: string[] = [];
    if (rule.mode === "manual") parts.push(`${fmt.money(shownAmount(rule.basePrice, basis))} base`);
    if (rule.mode === "percent") parts.push(`Cost + ${fmt.percent(toNumber(rule.markupValue))}`);
    if (rule.mode === "fixed") parts.push(`Cost + ${fmt.money(shownAmount(rule.markupValue, basis))}`);
    if (rule.mode === "parts") parts.push(toNumber(rule.markupValue) ? `Parts' prices + ${fmt.percent(toNumber(rule.markupValue))}` : "Parts' prices");
    if (rule.labourMinutes > 0) parts.push(`${duration(rule.labourMinutes)} labour`);
    if (rule.roundMode !== "none" && toNumber(rule.roundTo) > 0) {
        parts.push(`${ROUNDS[rule.roundMode]} ${fmt.money(rule.roundTo)}`);
    }
    return parts.join(" · ");
}

/**
 * The same, short enough for a table column: "Typed price", "+35%", "+$10.00 · 30 min labour · ↑ $5.00"
 * (↑ rounds up, ↓ down, ≈ to the nearest). A typed (manual) price with extras: "Manual · 20 min labour".
 */
export function describePricingShort(rule: PricingRule | undefined, basis: Basis, fmt: MoneyFormatter): string {
    if (!rule || isPlain(rule)) return "Typed price";
    const parts: string[] = [];
    if (rule.mode === "manual") parts.push("Manual");
    if (rule.mode === "percent") parts.push(toNumber(rule.markupValue) ? `+${fmt.percent(toNumber(rule.markupValue))}` : "Cost");
    if (rule.mode === "fixed") parts.push(toNumber(rule.markupValue) ? `+${fmt.money(shownAmount(rule.markupValue, basis))}` : "Cost");
    if (rule.mode === "parts") parts.push(toNumber(rule.markupValue) ? `Parts +${fmt.percent(toNumber(rule.markupValue))}` : "Parts");
    if (rule.labourMinutes > 0) parts.push(`${duration(rule.labourMinutes)} labour`);
    if (rule.roundMode !== "none" && toNumber(rule.roundTo) > 0) parts.push(`${ROUNDS_SHORT[rule.roundMode]} ${fmt.money(rule.roundTo)}`);
    return parts.join(" · ");
}
